import { apiClient } from "@/services/apiClient";

export interface EnterpriseCatalogEntry {
  slug: string;
  displayName: string;
  description: string;
  category: string;
  amountMinor: number;
  currency: "UYU" | "USD";
  graph: import("@/services/platformEnterprisesService").ProductGraph | null;
  enterprise: {
    id: string;
    slug: string;
    name: string;
    logoUrl: string | null;
    logoUrlDark: string | null;
    primaryColor: string | null;
    primaryColorDark: string | null;
  } | null;
  source: "enterprise";
}

export function getEnterpriseCatalog(category?: string): Promise<EnterpriseCatalogEntry[]> {
  const params = category ? { category } : undefined;
  return apiClient
    .get<EnterpriseCatalogEntry[]>("/me/certificate-pricing/enterprise-catalog", { params })
    .then((res) => res.data);
}

export interface CreateEnterpriseRequestPayload {
  productSlug: string;
  payload: Record<string, unknown>;
}

export interface EnterpriseRequest {
  id: string;
  enterpriseId: string;
  userId: string;
  productSlug: string;
  product?: { id: string; displayName: string } | null;
  status: string;
  payload: Record<string, unknown>;
  workflowState:
    | import("@/services/platformEnterprisesService").GraphState
    | null;
  graph?: import("@/services/platformEnterprisesService").ProductGraph | null;
  computedPriceMinor: number | null;
  computedPriceCurrency: string | null;
  parentRequestId: string | null;
  parentRequestType: string | null;
  /** Present when the current user is a participant (not the initiator). */
  myParticipant?: { inviteNodeId: string | null; role: string } | null;
  /** Requester user info — included in detail responses. */
  user?: { id: string; firstName?: string | null; lastName?: string | null; email: string } | null;
  /** Enterprise name — included in detail responses. */
  enterpriseName?: string;
  /** Participants — included in list responses. */
  participants?: Array<{
    id: string;
    email: string;
    role: string;
    status: string;
    user?: { id: string; firstName?: string | null; lastName?: string | null; email: string } | null;
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface EnterpriseRequestEvent {
  id: string;
  requestId: string;
  type: string;
  data: Record<string, unknown>;
  actorUserId: string | null;
  createdAt: string;
}

export function createEnterpriseRequest(
  productSlug: string,
  payload: Record<string, unknown>,
): Promise<EnterpriseRequest> {
  return apiClient
    .post<EnterpriseRequest>("/me/enterprise-requests", { productSlug, payload })
    .then((res) => res.data);
}

export function listMyEnterpriseRequests(): Promise<EnterpriseRequest[]> {
  return apiClient
    .get<EnterpriseRequest[]>("/me/enterprise-requests")
    .then((res) => res.data);
}

export function getMyEnterpriseRequest(id: string): Promise<EnterpriseRequest> {
  return apiClient
    .get<EnterpriseRequest>(`/me/enterprise-requests/${id}`)
    .then((res) => res.data);
}

export function getMyEnterpriseRequestEvents(id: string): Promise<EnterpriseRequestEvent[]> {
  return apiClient
    .get<EnterpriseRequestEvent[]>(`/me/enterprise-requests/${id}/events`)
    .then((res) => res.data);
}

export function completeMyEnterpriseRequestStep(
  requestId: string,
  stepKey: string,
  data?: Record<string, unknown>,
): Promise<EnterpriseRequest> {
  return apiClient
    .post<EnterpriseRequest>(`/me/enterprise-requests/${requestId}/complete-step`, {
      stepKey,
      data,
    })
    .then((res) => res.data);
}

export function uploadEnterpriseRequestDocument(
  requestId: string,
  nodeId: string,
  file: File,
): Promise<EnterpriseRequest> {
  const formData = new FormData();
  formData.append("file", file);
  return apiClient
    .post<EnterpriseRequest>(
      `/me/enterprise-requests/${requestId}/upload-document/${nodeId}`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    )
    .then((res) => res.data);
}

export async function uploadMyCollectionDocument(
  requestId: string,
  nodeId: string,
  documentKey: string,
  file: File,
): Promise<EnterpriseRequest> {
  const formData = new FormData();
  formData.append("file", file);
  return apiClient
    .post<EnterpriseRequest>(
      `/me/enterprise-requests/${requestId}/upload-collection-doc/${nodeId}?documentKey=${encodeURIComponent(documentKey)}`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    )
    .then((res) => res.data);
}

export async function uploadEnterpriseRequestBlob(
  requestId: string,
  file: File,
): Promise<{ fileUrl: string; fileName: string; fileMimeType: string }> {
  const formData = new FormData();
  formData.append("file", file);
  const res = await apiClient.post<{ fileUrl: string; fileName: string; fileMimeType: string }>(
    `/me/enterprise-requests/${requestId}/upload-blob`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return res.data;
}

export async function fetchMyEnterpriseDocumentBlob(
  requestId: string,
  nodeId: string,
): Promise<Blob> {
  const res = await apiClient.get(
    `/me/enterprise-requests/${requestId}/document/${nodeId}`,
    { responseType: "blob" },
  );
  return res.data as Blob;
}

export function cancelMyEnterpriseRequest(id: string): Promise<void> {
  return apiClient
    .post(`/me/enterprise-requests/${id}/cancel`)
    .then(() => undefined);
}

export function replyToEnterpriseInfoRequest(
  id: string,
  message: string,
  attachments?: Array<{ label: string; fileUrl: string; fileName: string; fileMimeType: string }>,
): Promise<void> {
  return apiClient
    .post(`/me/enterprise-requests/${id}/reply`, {
      message,
      ...(attachments?.length ? { attachments } : {}),
    })
    .then(() => undefined);
}

export function linkChildToEnterpriseRequest(
  requestId: string,
  stepKey: string,
  childRequestId: string,
  actorIndex?: number,
): Promise<EnterpriseRequest> {
  return apiClient
    .post<EnterpriseRequest>(`/me/enterprise-requests/${requestId}/link-child`, {
      stepKey,
      childRequestId,
      ...(actorIndex != null ? { actorIndex } : {}),
    })
    .then((res) => res.data);
}

export interface InternalRequestDefaults {
  fulfillmentMode?: 'digital_only' | 'physical_only' | 'digital_and_physical';
  periodMonths?: 6 | 12 | 24;
  presentingEntityAsEnterprise?: boolean;
  specialFormatTemplateUrl?: string;
  specialFormatTemplateFilename?: string;
}

export interface NodeDefaultsResponse {
  defaults: InternalRequestDefaults | null;
  enterpriseName: string;
}

export function getEnterpriseRequestNodeDefaults(
  requestId: string,
  nodeId: string,
): Promise<NodeDefaultsResponse> {
  return apiClient
    .get<NodeDefaultsResponse>(
      `/me/enterprise-requests/${requestId}/node-defaults/${nodeId}`,
    )
    .then((res) => res.data);
}

export interface EnterpriseRequestParticipant {
  id: string;
  enterpriseRequestId: string;
  userId: string | null;
  email: string;
  role: 'initiator' | 'participant';
  status: 'pending' | 'accepted' | 'declined';
  childRequestId: string | null;
  inviteNodeId: string | null;
  user?: {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    email: string;
  } | null;
  createdAt: string;
}

export function getEnterpriseRequestParticipants(
  requestId: string,
): Promise<EnterpriseRequestParticipant[]> {
  return apiClient
    .get<EnterpriseRequestParticipant[]>(`/me/enterprise-requests/${requestId}/participants`)
    .then((res) => res.data);
}

export function resendParticipantInvite(
  requestId: string,
  participantId: string,
): Promise<void> {
  return apiClient
    .post(`/me/enterprise-requests/${requestId}/participants/${participantId}/resend`)
    .then(() => undefined);
}

export function cancelParticipantInvite(
  requestId: string,
  participantId: string,
): Promise<void> {
  return apiClient
    .post(`/me/enterprise-requests/${requestId}/participants/${participantId}/cancel`)
    .then(() => undefined);
}
